/**
 * Table-half seam implementations.
 *
 * The mount merges this `Partial<ReadDeps & WriteDeps>` with the Calendar and
 * Feed halves into `installCapabilities`. Only the four members owned by the
 * Table lane are implemented here: `findArticles`, `createArticle`,
 * `publishArticle`, `regenerate`.
 *
 * Everything goes through `blogCreationService`, the same layer the forms use.
 * The spec warns that UI and tool paths "may have different validation logic"
 * (`index.bs:1598`), which is why there is no parallel fetch in this file and
 * no call into a page handler.
 */
import { blogCreationService } from '@/services/blogCreationService';
import { contentPlannerService } from '@/services/contentPlannerService';
import type { ReadDeps, WriteDeps, PlannerArticleSummary, Receipt, Refusal, TenantId } from './contract';
import { dispatchAndWait } from './handshake';
import { isRefusal, parseRequiredIsoDate, toCalendarDate } from './plannerDates';

/** Rows the agent may act on. The service pages; the tool caps the render. */
const LIST_PAGE_SIZE = 100;

/**
 * Rows pulled from the calendar to date the summary rows. The endpoint caps at
 * 500 and already applies the window server-side, so this is the whole window
 * for every workspace the planner has today.
 */
const CALENDAR_JOIN_LIMIT = 500;

/**
 * Publishing is the one place where a stale table is dangerous rather than
 * merely untidy: a bulk publish that returns before the rows repaint invites
 * the agent to re-read old state and publish twice. Each write therefore waits
 * for the page to acknowledge the commit.
 */
const TABLE_COMMITTED = 'planner-table-committed';

/**
 * THE SUMMARY ENDPOINT CARRIES NO CALENDAR DATE.
 *
 * This used to read `row.scheduled_date`, which `GET /blog-creation/content/
 * summary` has never returned: `ContentSummary` (`api/schemas/content.py:403`)
 * carries `scheduled_publish_time`, the WordPress publish instant, and nothing
 * else date-shaped. So `calendarDate` was null on every row and every windowed
 * query dropped every row it was given. The date comes from the calendar
 * endpoint instead, keyed by the same `blog_content.id`, and is passed in.
 */
function toSummary(
  row: {
    id: string;
    title?: string | null;
    keyword?: string | null;
    status?: string | null;
    wordpress_post_url?: string | null;
  },
  calendarDates: ReadonlyMap<string, string> | null,
): PlannerArticleSummary {
  return {
    id: row.id,
    title: row.title || row.keyword || '(untitled)',
    status: row.status ?? 'unknown',
    calendarDate: calendarDates?.get(row.id) ?? null,
    hasWordPressPost: row.wordpress_post_url != null,
  };
}

/**
 * The calendar's own placement date for every article in the window.
 *
 * `GET /content-planner/calendar` derives it as
 * `date(coalesce(scheduled_publish_at, scheduled_publish_time, created_at))`
 * (`api/repositories/content.py:1132`) and filters on that same expression, so
 * the window is applied by the source that owns the date rather than
 * re-derived here. Item ids are `str(BlogContent.id)` (`:1354`), the ids the
 * summary rows carry, which is what makes this a join and not a second list.
 *
 * A failure here is left to throw. Answering an empty map would drop every row
 * and render as "No articles matched", which is the lie this whole change is
 * about.
 */
async function calendarDatesInWindow(
  tenant: TenantId,
  from: Date | undefined,
  to: Date | undefined,
): Promise<ReadonlyMap<string, string>> {
  const res = await contentPlannerService.getCalendarItems({
    tenantId: tenant,
    start_date: from ? toCalendarDate(from) : undefined,
    end_date: to ? toCalendarDate(to) : undefined,
    limit: CALENDAR_JOIN_LIMIT,
  });
  const dates = new Map<string, string>();
  for (const item of res.items ?? []) {
    if (item.scheduled_date) dates.set(item.id, item.scheduled_date);
  }
  return dates;
}

export const tableSeams: Partial<ReadDeps & WriteDeps> = {
  /* ---------------------------------------------------------------------- */
  /* SEAM: findArticles — the only read, shared by all three lanes           */
  /* ---------------------------------------------------------------------- */
  async findArticles(tenant, query) {
    // `list` takes ONE status string; the tool accepts an array, so a
    // multi-status query is filtered client-side after a single unfiltered
    // fetch rather than fanned out into N requests.
    const singleStatus = query.status?.length === 1 ? query.status[0] : undefined;
    const res = await blogCreationService.list(
      {
        status: singleStatus,
        search: query.titleContains,
        page: 1,
        page_size: query.limit ?? LIST_PAGE_SIZE,
      },
      undefined,
      tenant,
    );

    // An absent window is no date filter, so the calendar is not read at all:
    // a list with no dates in it must not cost a second request, and must not
    // be narrowed by a window the caller never asked for.
    const windowed = query.from !== undefined || query.to !== undefined;
    const calendarDates = windowed
      ? await calendarDatesInWindow(tenant, query.from, query.to)
      : null;

    const wanted = query.status?.length ? new Set(query.status) : null;
    const rows: PlannerArticleSummary[] = [];
    for (const raw of res.items ?? []) {
      const row = toSummary(raw as Parameters<typeof toSummary>[0], calendarDates);
      if (wanted && !wanted.has(row.status)) continue;
      if (calendarDates) {
        // A row with no calendar date cannot satisfy a date window. Excluding
        // it is the honest answer; including it would let a date-filtered
        // agent act on rows outside the window it asked for.
        if (!row.calendarDate) continue;
        // Parsed as a LOCAL civil date, matching the endpoints `plannerDates`
        // builds. `new Date('2026-09-18T00:00:00Z')` is the 17th at 18:00 in
        // America/Chicago, so a UTC parse drops every row sitting exactly on
        // `from`.
        const d = parseRequiredIsoDate(row.calendarDate, 'scheduled_date');
        if (isRefusal(d)) continue;
        if (query.from && d < query.from) continue;
        if (query.to && d > query.to) continue;
      }
      rows.push(row);
    }
    return rows;
  },

  /* ---------------------------------------------------------------------- */
  /* SEAM: createArticle — spends budget                                     */
  /* ---------------------------------------------------------------------- */
  async createArticle(auth, draft): Promise<Receipt | Refusal> {
    const created = await blogCreationService.create({
      title: draft.title,
      keyword: draft.keyword,
      // Named explicitly. The wizard's own payload omits it and the server
      // default is relied on; a tool states its intent rather than inheriting
      // whatever the backend happens to default to today.
      wordpress_publish_mode: draft.wordpressPublishMode,
    } as Parameters<typeof blogCreationService.create>[0]);

    await settle(created.id, 'created');
    return {
      summary: `Created "${draft.title}" (${created.id}). Generation queued; dest ${draft.wordpressPublishMode}.`,
      facts: { id: created.id, tenant: auth.tenant, dest: draft.wordpressPublishMode },
    };
  },

  /* ---------------------------------------------------------------------- */
  /* SEAM: publishArticle — writes to the live client site                   */
  /* ---------------------------------------------------------------------- */
  async publishArticle(auth, articleId): Promise<Receipt | Refusal> {
    // Safe to call because the command's `resolve` already evaluated
    // `buildGateItem` and refused on violations: `confirmPublishWithAcknowledgement`
    // short-circuits at prePublishGate.ts:145 when nothing is flagged, so no
    // human click is awaited. Never routes through `autoPublish`, which has no
    // gate at all.
    const res = await blogCreationService.publish(articleId, undefined, undefined, undefined, auth.tenant);
    await settle(articleId, 'published');
    return {
      summary: `Published ${articleId}${res?.post_url ? ` → ${res.post_url}` : ''} (${res?.status ?? 'unknown'}).`,
      facts: {
        id: articleId,
        url: res?.post_url ?? '',
        wordpressPostId: res?.wordpress_post_id ?? 0,
        status: res?.status ?? 'unknown',
      },
    };
  },

  /* ---------------------------------------------------------------------- */
  /* SEAM: regenerate — spends budget, and must not inherit stored settings  */
  /* ---------------------------------------------------------------------- */
  async regenerate(auth, articleId, options): Promise<Receipt | Refusal> {
    // BOTH options are passed on EVERY call, never conditionally. The service
    // used to post `{ stage }` alone, and the backend then replayed the row's
    // stored writer and publish mode — which is how a production article was
    // regenerated onto the wrong writer, and how a restart of a
    // previously-published row can push a draft live. Naming them is the
    // entire point of this seam.
    const updated = await blogCreationService.regenerate(articleId, 'all' as Parameters<typeof blogCreationService.regenerate>[1], {
      modelOverride: options.modelOverride,
      wordpressPublishMode: options.wordpressPublishMode,
    });
    await settle(articleId, 'restarted');
    return {
      summary:
        `Restarted ${articleId} on ${options.modelOverride}, dest ${options.wordpressPublishMode}. ` +
        `Status ${updated?.status ?? 'pending'}. The writer is only confirmed on a terminal read.`,
      facts: {
        id: articleId,
        writer: options.modelOverride,
        dest: options.wordpressPublishMode,
        tenant: auth.tenant,
      },
    };
  },
};

/**
 * Wait for the table to repaint before reporting success.
 * `ContentPlannerPage` emits `tool-completion-${requestId}` after commit.
 */
async function settle(id: string, what: string): Promise<void> {
  try {
    await dispatchAndWait(TABLE_COMMITTED, { id, what });
  } catch {
    // A missed handshake means the UI did not confirm in time. The write
    // already landed, so this is not an error to surface as a failure; the
    // receipt still returns and the agent re-reads through find_articles.
  }
}

/** The mount merges seam files by name; it takes the default export. */
export default tableSeams;
